import { useEffect, useState } from 'react'
import { Alert, Layout, Menu, Button, Dropdown, Space, Typography } from 'antd'
import { Link, useLocation } from 'react-router-dom'
import {
  DashboardOutlined,
  TeamOutlined,
  SwapOutlined,
  FileTextOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined
} from '@ant-design/icons'
import { useAuth } from '../contexts/AuthContext'
import { VERSION } from '../version'
import { BACKEND_STATUS_EVENT } from '../services/api'

const { Header, Sider, Content } = Layout
const { Text } = Typography

function AppLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [backendRestarting, setBackendRestarting] = useState(false)
  const location = useLocation()
  const { user, logout } = useAuth()

  useEffect(() => {
    const handler = (e) => {
      const down = Boolean(e?.detail?.down)
      setBackendRestarting(down)
    }
    window.addEventListener(BACKEND_STATUS_EVENT, handler)
    return () => window.removeEventListener(BACKEND_STATUS_EVENT, handler)
  }, [])

  // Stage 2: customers and item mappings are now sourced from the SKU DB, so the
  // Customers and Item Mappings tabs are hidden - only Dashboard and Transactions
  // remain. Routes are kept in App.jsx (reachable by direct URL) so this is easily
  // reversible: uncomment the entries below to restore the tabs.
  const menuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: <Link to="/">Dashboard</Link>
    },
    // {
    //   key: '/customers',
    //   icon: <TeamOutlined />,
    //   label: <Link to="/customers">Customers</Link>
    // },
    // {
    //   key: '/item-mappings',
    //   icon: <SwapOutlined />,
    //   label: <Link to="/item-mappings">Item Mappings</Link>
    // },
    {
      key: '/transactions',
      icon: <FileTextOutlined />,
      label: <Link to="/transactions">Transactions</Link>
    }
  ]

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: logout
    }
  ]

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        breakpoint="lg"
        collapsedWidth={isMobile ? 0 : 80}
        onBreakpoint={(broken) => {
          setIsMobile(broken)
          if (broken) {
            setCollapsed(true)
          }
        }}
        onCollapse={(value) => setCollapsed(value)}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 1001
        }}
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            padding: '8px',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              fontSize: collapsed ? 16 : 18,
              fontWeight: 'bold',
              lineHeight: 1.2,
              textAlign: 'center',
              whiteSpace: 'nowrap'
            }}
          >
            {collapsed ? 'EMEW' : 'EMEW PO'}
          </div>
          {!collapsed && (
            <div
              style={{
                fontSize: 10,
                opacity: 0.7,
                marginTop: 2
              }}
            >
              v{VERSION}
            </div>
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={() => {
            if (isMobile) {
              setCollapsed(true)
            }
          }}
        />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? (isMobile ? 0 : 80) : 200, transition: 'all 0.2s' }}>
        <Header
          style={{
            padding: isMobile ? '0 12px' : '0 24px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            width: '100%',
            boxShadow: '0 1px 4px rgba(0,21,41,.08)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: '16px',
                width: 64,
                height: 64
              }}
            />
            {isMobile && !collapsed && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0,0,0,0.45)',
                  zIndex: 1000
                }}
                onClick={() => setCollapsed(true)}
              />
            )}
          </div>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Button type="text">
              <Space>
                <UserOutlined />
                {!isMobile && <Text>{user?.name || user?.email || 'Account'}</Text>}
              </Space>
            </Button>
          </Dropdown>
        </Header>
        <Content
          style={{
            margin: isMobile ? '12px 8px' : '24px 16px',
            padding: isMobile ? 12 : 24,
            minHeight: 280,
            background: '#f0f2f5'
          }}
        >
          {backendRestarting && (
            <Alert
              type="warning"
              showIcon
              message="Backend is restarting"
              description="Some actions may fail for a short time. Please wait ~10–30 seconds and try again."
              style={{ marginBottom: 16 }}
            />
          )}
          {children}
        </Content>
      </Layout>
    </Layout>
  )
}

export default AppLayout
